import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import DateRangeFilter from "@/components/DateRangeFilter";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Wallet, TrendingUp, TrendingDown } from "lucide-react";

export default function PlataformaGuias() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [resumen, setResumen] = useState({ depositos: [], gastos_pedidos: [], total_depositado: 0, total_gastado: 0, saldo_disponible: 0 });
  const [bancos, setBancos] = useState([]);
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    monto: 0,
    banco: "",
    referencia: "",
    notas: "",
  });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const [r, b] = await Promise.all([
        api.get("/plataforma-guias/resumen", { params }),
        api.get("/bancos"),
      ]);
      setResumen(r.data);
      setBancos((b.data || []).filter((x) => x.activo !== false));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.monto || form.monto <= 0) return toast.error("Monto > 0 requerido");
    if (!form.banco) return toast.error("Selecciona el banco desde el que se paga");
    try {
      await api.post("/plataforma-guias/depositos", form);
      toast.success("Depósito registrado + gasto creado como proveedor");
      setForm({ ...form, monto: 0, referencia: "", notas: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este depósito? (también eliminará el gasto asociado)")) return;
    try {
      await api.delete(`/plataforma-guias/depositos/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="plataforma-guias-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Finanzas</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Truck className="w-8 h-8 text-blue-700" /> Plataforma de Guías
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Saldo acreditado a la plataforma de envíos vs. costo real de las guías. Los depósitos se registran automáticamente como gasto de proveedor.
          </p>
        </div>
        <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="pg" />
      </header>

      {/* Saldos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-blue-200 bg-blue-50 rounded-md" data-testid="pg-total-depositado">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Total depositado
          </div>
          <div className="font-manrope font-black text-3xl mt-1 text-blue-700">{formatMoney(resumen.total_depositado)}</div>
        </Card>
        <Card className="p-4 border-orange-200 bg-orange-50 rounded-md" data-testid="pg-total-gastado">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
            <TrendingDown className="w-3 h-3" /> Gastado en guías
          </div>
          <div className="font-manrope font-black text-3xl mt-1 text-orange-700">{formatMoney(resumen.total_gastado)}</div>
        </Card>
        <Card className={`p-4 rounded-md border-2 ${resumen.saldo_disponible >= 0 ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="pg-saldo">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Saldo disponible
          </div>
          <div className={`font-manrope font-black text-3xl mt-1 ${resumen.saldo_disponible >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatMoney(resumen.saldo_disponible)}</div>
        </Card>
      </div>

      {/* Formulario Depósito */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-3 h-3" /> Registrar depósito a la plataforma
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div><Label className="text-xs">Fecha *</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="pg-fecha" /></div>
          <div><Label className="text-xs">Monto (Q) *</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: Number(e.target.value) })} data-testid="pg-monto" /></div>
          <div>
            <Label className="text-xs">Banco (paga desde) *</Label>
            <Select value={form.banco} onValueChange={(v) => setForm({ ...form, banco: v })}>
              <SelectTrigger data-testid="pg-banco"><SelectValue placeholder="Banco" /></SelectTrigger>
              <SelectContent>
                {bancos.length === 0 ? (
                  <SelectItem value="—" disabled>Configura bancos primero</SelectItem>
                ) : (
                  bancos.map((b) => <SelectItem key={b.id} value={b.nombre}>{b.nombre}</SelectItem>)
                )}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Referencia</Label><Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="pg-ref" /></div>
          <div><Label className="text-xs">Notas</Label><Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="pg-notas" /></div>
          <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="pg-save">Registrar</Button>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 italic">Se creará también automáticamente un gasto de proveedor "Plataforma Guías" en el módulo de Gastos.</p>
      </Card>

      {/* Depósitos */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Depósitos ({resumen.depositos.length})</div>
        {resumen.depositos.length === 0 ? (
          <div className="text-zinc-400 text-xs py-4 text-center">Sin depósitos registrados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Monto</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Banco</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Referencia</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Notas</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {resumen.depositos.map((d) => (
                  <tr key={d.id} className="hover:bg-zinc-50" data-testid={`pg-dep-${d.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{d.fecha}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-700">{formatMoney(d.monto)}</td>
                    <td className="px-3 py-2 text-xs">{d.banco || "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono">{d.referencia || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-600 max-w-xs truncate">{d.notas || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(d.id)} className="text-red-600 text-xs hover:underline inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Gastos de guías por pedido */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Costo de guías por pedido ({resumen.gastos_pedidos.length})</div>
        {resumen.gastos_pedidos.length === 0 ? (
          <div className="text-zinc-400 text-xs py-4 text-center">Sin pedidos con costo de guía registrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Pedido</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Cliente</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Guía</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Costo (Q)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {resumen.gastos_pedidos.map((o) => (
                  <tr key={o.id} className="hover:bg-zinc-50">
                    <td className="px-3 py-2">
                      <Link to={`/pedidos/${o.id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline">{o.numero}</Link>
                    </td>
                    <td className="px-3 py-2 text-xs">{o.cliente_nombre}</td>
                    <td className="px-3 py-2 text-xs font-mono">{o.numero_guia || <span className="text-zinc-400">— sin asignar</span>}</td>
                    <td className="px-3 py-2 text-xs">{(o.costo_guia_at || o.numero_guia_at) ? formatDate(o.costo_guia_at || o.numero_guia_at) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-orange-700 font-semibold">{formatMoney(o.costo_guia)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
